import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./SelectColorInput.module.css";
import classNames from "classnames";
import { v4 } from "uuid";
import Color from "color";

import ColorInputModal from "../ColorInputModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import convertVariableColor from "NewsAdmin/utils/convertVariableColor";

/**
 * Interactive component.
 * An input item that allows a user to select a color.
 * The selected color is reflected by the input background.
 */
export function SelectColorInput({
  setRef,
  currentColor = "#ffffff",
  setCurrentColor,
  customColor,
  setCustomColor,
  labelText,
  className,
  isOpen,
  setIsOpen,
}) {
  const id = v4();
  const ref = React.createRef();

  useEffect(() => {
    if (setRef) {
      setRef(ref.current);
    }
  }, []);

  // Split currentColor and update when changed.
  const currentColorPair = currentColor;

  // Converts colors if stored as a root variable
  const convertedCurrent = currentColorPair;

  let backgroundColor = convertedCurrent;
  let inverseIconColor = backgroundColor;

  // Update to parse valid color hex or rgb strings
  const match = backgroundColor.match(
    /^(.+?)\s+(rgb\(.+\)|#.+)$|^(rgb\(.+\)|#.+)$/
  );

  if (match) {
    const color = match[2] ?? match[3]; // match[2] = from name+value, match[3] = from raw

    backgroundColor = color;
    inverseIconColor = Color(backgroundColor).isDark();
  } else {
    // Default to white background if parsing fails
    backgroundColor = "#fff";
    inverseIconColor = Color(backgroundColor).isDark();
  }

  return (
    <div ref={ref} className={classNames(styles.container, className)}>
      <label
        className={classNames(styles.label, {
          [styles.hidden]: !labelText,
        })}
        htmlFor={`color-input-${id}`}
      >
        {labelText ?? "Color Input"}
      </label>
      <div
        className={classNames(styles["button-container"], {
          [styles["hidden-modal"]]: !isOpen,
        })}
      >
        <input
          hidden
          defaultValue={currentColor}
          type="text"
          name={`color-input-${id}`}
          className="custom-color-input"
        />
        <button
          onClick={setIsOpen}
          style={{
            border: "1px solid #ccc",
            backgroundColor: backgroundColor,
          }}
          className={classNames(styles["dropdown-button"], {
            [styles.pair]: labelText,
          })}
        >
          <FontAwesomeIcon icon={faAngleDown} inverse={inverseIconColor} />
        </button>
        <ColorInputModal
          customColor={customColor}
          setCustomColor={setCustomColor}
          currentColor={currentColor}
          setCurrentColor={setCurrentColor}
        />
      </div>
    </div>
  );
}

SelectColorInput.propTypes = {
  /**
   * String that states what color is currently selected.
   * Should be in the format of `{name} {color}`.
   * Ex. "default #FFF", "empowerment #415364", "custom {color}".
   * Parent should provide a default to "default #FFF".
   */
  currentColor: PropTypes.string,
  /**
   * Function that sets the currentColor.
   * The value should be stored as `${e.target.title} ${e.target.style.backgroundColor}`.
   */
  setCurrentColor: PropTypes.func,
  /**
   * The value of the custom color.
   * Parent should provide a default to "#fff".
   */
  customColor: PropTypes.string,
  /**
   * Function that sets the value of customColor.
   */
  setCustomColor: PropTypes.func,
  /**
   * Optional text for the input label.
   */
  labelText: PropTypes.string,
  /**
   * Optional className.
   */
  className: PropTypes.string,
  /**
   * Determines if the modal is open.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the input is clicked.
   */
  setIsOpen: PropTypes.func,
};

SelectColorInput.defaultProps = {
  customColor: "#fff",
};
